import Layout from "@/components/Layout";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import PermissionDenied from "@/components/PermissionDenied";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import {
  BANK_TYPES,
  bankApiErrorMessage,
  bankTypeLabel,
  createBank,
  deleteBank,
  listBanks,
  syncBanksFromApi,
  updateBank,
  type BankDraft,
  type BankRecord,
  type BankTypeValue,
} from "@/lib/dataBank/banksStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { useGridPagination } from "@/hooks/useGridPagination";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import {
  Building2,
  CalendarIcon,
  Edit,
  Filter,
  Hash,
  ImageIcon,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;
const LOGO_MAX_BYTES = 2_000_000;

const emptyDraft = (): BankDraft => ({
  name: "",
  shortName: "",
  code: "",
  swiftCode: "",
  bankType: "commercial",
  isActive: true,
  logo: "",
  logoFile: null,
  removeLogo: false,
});

type BankSortBy =
  | "latest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "sort_asc"
  | "sort_desc"
  | "updated_latest"
  | "updated_oldest";

function bankMetaLabel(b: BankRecord): string {
  const parts = [b.code, b.shortName].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : bankTypeLabel(b.bankType);
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

const Banks = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "bank_can_create");
  const canUpdate = can(user, "bank_can_update");
  const canDelete = can(user, "bank_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<BankSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [filterCode, setFilterCode] = useState("");
  const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("all");
  const [filterType, setFilterType] = useState<"all" | BankTypeValue>("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BankRecord | null>(null);
  const [draft, setDraft] = useState<BankDraft>(emptyDraft());
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<BankRecord | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const banks = useMemo(() => listBanks(), [version]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsBootstrapping(true);
      setIsForbidden(false);
      try {
        await syncBanksFromApi();
        if (mounted) refresh();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 403) {
          if (mounted) setIsForbidden(true);
          return;
        }
        console.error("[Banks] Load failed", error);
        if (mounted) toast.error("Could not fetch banks. Showing cached data if any.");
      } finally {
        if (mounted) setIsBootstrapping(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nameNeedle = filterName.trim().toLowerCase();
    const codeNeedle = filterCode.trim().toLowerCase();

    return banks.filter((b) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(b.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(b.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (nameNeedle && !b.name.toLowerCase().includes(nameNeedle)) return false;
      if (codeNeedle && !b.code.toLowerCase().includes(codeNeedle)) return false;
      if (filterActive === "active" && !b.isActive) return false;
      if (filterActive === "inactive" && b.isActive) return false;
      if (filterType !== "all" && b.bankType !== filterType) return false;
      const matchesQuery =
        !q ||
        b.name.toLowerCase().includes(q) ||
        b.shortName.toLowerCase().includes(q) ||
        b.code.toLowerCase().includes(q) ||
        b.swiftCode.toLowerCase().includes(q) ||
        bankTypeLabel(b.bankType).toLowerCase().includes(q);
      return matchesQuery;
    });
  }, [banks, query, filterCreatedDate, quickDate, filterName, filterCode, filterActive, filterType]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "sort_asc":
          return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
        case "sort_desc":
          return b.sortOrder - a.sortOrder || a.name.localeCompare(b.name);
        case "updated_oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "updated_latest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterCode.trim() ? 1 : 0) +
    (filterActive !== "all" ? 1 : 0) +
    (filterType !== "all" ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterCode("");
    setFilterActive("all");
    setFilterType("all");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterCode}|${filterActive}|${filterType}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft());
    setLogoPreview(null);
    setModalOpen(true);
  };

  const openEdit = (b: BankRecord) => {
    setEditing(b);
    setDraft({
      name: b.name,
      shortName: b.shortName,
      code: b.code,
      swiftCode: b.swiftCode,
      bankType: b.bankType,
      isActive: b.isActive,
      logo: b.logo,
      logoFile: null,
      removeLogo: false,
    });
    setLogoPreview(b.logo || null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setDraft(emptyDraft());
    setLogoPreview(null);
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const clearLogo = () => {
    setLogoPreview(null);
    setDraft((d) => ({ ...d, logo: "", logoFile: null, removeLogo: true }));
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const onLogoFile = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG, WebP).");
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error(`Logo must be under ${Math.round(LOGO_MAX_BYTES / 1_000_000)} MB.`);
      return;
    }
    try {
      const url = await readFileAsDataUrl(file);
      setLogoPreview(url);
      setDraft((d) => ({ ...d, logoFile: file, removeLogo: false }));
    } catch {
      toast.error("Could not read the logo file. Try another image.");
    }
  };

  const saveBank = async () => {
    if (isSaving) return;
    if (!draft.name.trim()) {
      toast.error("Bank name is required.");
      return;
    }
    if (!draft.bankType) {
      toast.error("Bank type is required.");
      return;
    }
    setIsSaving(true);
    try {
      const payload: BankDraft = {
        ...draft,
        name: draft.name.trim(),
        shortName: draft.shortName.trim(),
        code: draft.code.trim(),
        swiftCode: draft.swiftCode.trim(),
      };
      if (editing) {
        const result = await updateBank(editing.id, payload);
        if (!result) {
          toast.error("Bank not found.");
          return;
        }
        toast.success(result.message);
      } else {
        const result = await createBank(payload);
        toast.success(result.message);
      }
      closeModal();
      refresh();
    } catch (error) {
      console.error("[Banks] Save failed", error);
      toast.error(bankApiErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await deleteBank(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[Banks] Delete failed", error);
      toast.error(bankApiErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Banks access required"
      message="You don’t have permission to view banks. Ask an administrator to grant bank_can_view_any."
    />
  ) : (
    <Layout title="Banks">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Banks</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage bank master data for payments and receipts.{" "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Add bank
            </Button>
          ) : null}
        </div>

        <Dialog
          open={modalOpen}
          onOpenChange={(o) => {
            if (!o) closeModal();
            else setModalOpen(true);
          }}
        >
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit bank" : "Add bank"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-1">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Bank name <span className="text-destructive">*</span>
                </Label>
                <Input
                  className="glass-input h-10"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="e.g. Habib Bank Limited"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Short name</Label>
                  <Input
                    className="glass-input h-10"
                    value={draft.shortName}
                    onChange={(e) => setDraft((d) => ({ ...d, shortName: e.target.value }))}
                    placeholder="HBL"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Code</Label>
                  <Input
                    className="glass-input h-10"
                    value={draft.code}
                    onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
                    placeholder="HBL"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">SWIFT</Label>
                  <Input
                    className="glass-input h-10"
                    value={draft.swiftCode}
                    onChange={(e) => setDraft((d) => ({ ...d, swiftCode: e.target.value }))}
                    placeholder="HABBPKKA"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Type <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={draft.bankType}
                    onValueChange={(v) => setDraft((d) => ({ ...d, bankType: v as BankTypeValue }))}
                  >
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BANK_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {bankTypeLabel(t)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-border/50 bg-muted/10 px-3 py-2.5">
                <Checkbox
                  checked={draft.isActive}
                  onCheckedChange={(c) => setDraft((d) => ({ ...d, isActive: c !== false }))}
                  id="bank-active"
                />
                <Label htmlFor="bank-active" className="text-sm font-normal cursor-pointer">
                  Active — show in payment pickers
                </Label>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Logo (optional)</Label>
                <div className="flex items-center gap-3 rounded-xl border border-dashed border-border/60 bg-muted/10 p-3">
                  <div className="relative shrink-0">
                    <div className="h-14 w-14 rounded-xl border border-border/50 bg-background overflow-hidden flex items-center justify-center">
                      {logoPreview ? (
                        <img src={logoPreview} alt="" className="h-full w-full object-contain p-1.5" />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-muted-foreground/40" />
                      )}
                    </div>
                    {logoPreview ? (
                      <Button
                        type="button"
                        size="icon"
                        variant="secondary"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full shadow-md"
                        onClick={clearLogo}
                        aria-label="Remove logo"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => void onLogoFile(e.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      variant="glass"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {logoPreview ? "Change" : "Upload"}
                    </Button>
                    <p className="text-[11px] text-muted-foreground mt-1.5">PNG, JPG, WebP · max 2 MB</p>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="glass" onClick={closeModal} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void saveBank()} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, SWIFT, or type…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter banks</SheetTitle>
                  <SheetDescription>Name, code, type, status, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Bank</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Code contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterCode}
                        onChange={(e) => setFilterCode(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Type</Label>
                      <Select value={filterType} onValueChange={(v) => setFilterType(v as typeof filterType)}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All types</SelectItem>
                          {BANK_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {bankTypeLabel(t)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Status</Label>
                      <Select value={filterActive} onValueChange={(v) => setFilterActive(v as typeof filterActive)}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="active">Active only</SelectItem>
                          <SelectItem value="inactive">Inactive only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="flex-1"
                              onClick={() => setFilterCreatedDate(undefined)}
                            >
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={resetSheetFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="bank-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as BankSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="sort_asc">Sort order: low</SelectItem>
                  <SelectItem value="sort_desc">Sort order: high</SelectItem>
                  <SelectItem value="updated_latest">Recently updated</SelectItem>
                  <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="bank-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[200px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as BankSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="sort_asc">Sort order: low</SelectItem>
                    <SelectItem value="sort_desc">Sort order: high</SelectItem>
                    <SelectItem value="updated_latest">Recently updated</SelectItem>
                    <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isBootstrapping ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading banks…
          </div>
        ) : null}

        {!isBootstrapping && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {banks.length === 0 ? "No banks yet. Click “Add bank” to create one." : "No banks match your search."}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <CatalogEntityCard
                  title={b.name}
                  avatarUrl={b.logo || null}
                  titleIcon={Building2}
                  titleIconClassName="text-sky-600"
                  primaryMeta={{
                    label: "Code",
                    value: bankMetaLabel(b),
                    icon: Hash,
                    iconClassName: "text-sky-600",
                  }}
                  secondaryMeta={{
                    label: "Type",
                    value: bankTypeLabel(b.bankType),
                    icon: Building2,
                    iconClassName: "text-emerald-600",
                  }}
                  footer={`${b.isActive ? "Active" : "Inactive"} · Created ${formatCatalogAuditDate(b.createdAt)}`}
                  onEdit={canUpdate ? () => openEdit(b) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(b) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    {b.logo ? (
                      <img src={b.logo} alt="" className="h-full w-full object-contain p-1" />
                    ) : (
                      <Building2 className="w-8 h-8 text-sky-600/50" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">{b.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono">{bankMetaLabel(b)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {b.isActive ? "Active" : "Inactive"} · {bankTypeLabel(b.bankType)} · Updated{" "}
                      {formatCatalogAuditDate(b.updatedAt)}
                    </p>
                  </div>
                  <div className="text-right ml-auto">
                    <p className="text-[11px] text-muted-foreground font-mono">{b.swiftCode || "—"}</p>
                    <p className="text-[11px] text-muted-foreground">Created {formatCatalogAuditDate(b.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(b)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => setDeleteTarget(b)}
                        aria-label="Delete bank"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this bank?"
          description={
            deleteTarget ? (
              <>
                Remove <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? Payments that
                reference this bank may be affected.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
};

export default Banks;
